import {
    getVehicleCardDefaultImageWidthForLayout,
    getVehicleCardImageSizesForLayout,
    getVehicleCardImageWidthsForLayout,
    getVehicleImageSrcSet,
    getVehicleImageUrl,
} from "@/helpers/imageKit";
import type { VehicleAlgoliaRecord } from "@/types";

const MAX_DISPLAY_PHOTOS = 5;
const SWIPE_LOCK_PX = 8;
const SWIPE_RATIO = 0.2;
const SLIDE_TRANSITION_MS = 300;
const RESISTANCE_RATIO = 0.35;
const DEFERRED_CAROUSEL_ROOT_MARGIN = "200px";

type PhotoSource = {
    src: string;
    srcset?: string;
    sizes?: string;
};

function parsePhotoKeys(card: HTMLElement): string[] {
    const raw = card.dataset.photoKeys;
    if (!raw) return [];

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .map((item) => String(item).trim())
            .filter((item) => item !== "");
    } catch {
        return [];
    }
}

function vehicleImageContext(
    card: HTMLElement,
    photos: string[],
): VehicleAlgoliaRecord {
    const useLocal = card.dataset.localPhotos === "1";

    return {
        local_photos: useLocal ? photos : [],
        photos: useLocal ? [] : photos,
    } as VehicleAlgoliaRecord;
}

export function buildPhotoSourcesForCard(
    card: HTMLElement,
    photos: string[],
): PhotoSource[] {
    if (photos.length === 0) return [];

    const layout = card.dataset.imageLayout ?? "default";
    const vehicle = vehicleImageContext(card, photos);
    const widths = getVehicleCardImageWidthsForLayout(layout);
    const defaultWidth = getVehicleCardDefaultImageWidthForLayout(layout);
    const sizes = getVehicleCardImageSizesForLayout(layout);

    return photos.map((photo) => ({
        src: getVehicleImageUrl(vehicle, photo, { width: defaultWidth }),
        srcset: getVehicleImageSrcSet(vehicle, photo, widths),
        sizes,
    }));
}

export function appendPhotoSourceSpans(
    photosEl: HTMLElement,
    card: HTMLElement,
    photos: string[],
): void {
    photosEl.replaceChildren(
        ...buildPhotoSourcesForCard(card, photos).map((photo) => {
            const span = document.createElement("span");
            span.dataset.src = photo.src;
            if (photo.srcset) span.dataset.srcset = photo.srcset;
            if (photo.sizes) span.dataset.sizes = photo.sizes;
            return span;
        }),
    );
}

/** Build hidden carousel spans from compact `data-photo-keys` (SSR below the fold). */
export function hydrateDeferredCarouselPhotos(
    card: HTMLElement,
    photosEl: HTMLElement,
): boolean {
    if (photosEl.querySelector("span")) return true;

    const photos = parsePhotoKeys(card);
    if (photos.length === 0) return false;

    appendPhotoSourceSpans(photosEl, card, photos);

    delete photosEl.dataset.deferred;
    return true;
}

function readPhotoSources(
    photosEl: HTMLElement,
    card?: HTMLElement,
): PhotoSource[] {
    const fromSpans = Array.from(photosEl.querySelectorAll("span"))
        .map((span): PhotoSource => ({
            src: span.dataset.src ?? span.textContent?.trim() ?? "",
            srcset: span.dataset.srcset,
            sizes: span.dataset.sizes,
        }))
        .filter((photo) => photo.src !== "");

    if (fromSpans.length > 0) return fromSpans;

    if (!card) return [];

    return buildPhotoSourcesForCard(card, parsePhotoKeys(card));
}

function applyPhotoSources(
    img: HTMLImageElement,
    photo: PhotoSource,
): void {
    img.src = photo.src;

    if (photo.srcset) {
        img.srcset = photo.srcset;
        if (photo.sizes) {
            img.sizes = photo.sizes;
        } else {
            img.removeAttribute("sizes");
        }
    } else if (!img.getAttribute("srcset")) {
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
    }
}

export function getDisplayPhotos<T>(
    photos: T[],
    max: number = MAX_DISPLAY_PHOTOS,
): { displayPhotos: T[]; totalCount: number } {
    return {
        displayPhotos: photos.slice(0, max),
        totalCount: photos.length,
    };
}

export function resolveSlideIndex(
    requestedIdx: number,
    length: number,
): number {
    return ((requestedIdx % length) + length) % length;
}

const VEHICLE_CARD_LINK_SELECTORS = [
    ".vehicle-card__cta[href]",
    ".vehicle-card__model a[href]",
    ".slide-link[href]",
    ".vehicle-card__overlay__cta[href]",
] as const;

function isNavigableHref(href: string): boolean {
    return href !== "" && href !== "#" && !href.endsWith("#");
}

/** First usable vehicle detail URL on the card (for delegated navigation). */
export function resolveVehicleCardUrl(card: HTMLElement): string | null {
    for (const selector of VEHICLE_CARD_LINK_SELECTORS) {
        const el = card.querySelector<HTMLAnchorElement>(selector);
        const href = el?.getAttribute("href") ?? el?.href;
        if (href && isNavigableHref(href)) {
            return el?.href ?? href;
        }
    }

    return null;
}

/**
 * Clicks on non-interactive card areas (brand, specs, padding) navigate to the
 * vehicle page. Mirrors the Vue `data-link` pattern from app.scss (not loaded on Blade).
 */
export function setupVehicleCardNavigation(card: HTMLElement): void {
    if (card.dataset.cardNavInit === "true") return;
    card.dataset.cardNavInit = "true";

    card.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (
            target.closest(
                "button, .nav-button, .vehicle-card__compare-favorite, a",
            )
        ) {
            return;
        }

        const url = resolveVehicleCardUrl(card);
        if (url) {
            window.location.assign(url);
        }
    });
}

/**
 * Hydrates a Blade vehicle-card with an animated, swipeable photo carousel.
 *
 * The Blade partial only renders one slide; this helper clones it for each
 * additional photo, wraps them in a flex track, and translates the track
 * with CSS transitions. On touch, the track follows the finger and snaps
 * to the next/previous slide on release. Idempotent via `data-carousel-init`.
 */
function runVehicleCardCarouselInit(card: HTMLElement): void {
    if (card.dataset.carouselInit === "true") return;

    const container = card.querySelector<HTMLElement>(".swiper-container");
    const touchSurface =
        card.querySelector<HTMLElement>(".carousel-wrapper") ?? container;
    const photosEl = card.querySelector<HTMLElement>(".vehicle-card__photos");
    if (!container || !touchSurface || !photosEl) return;

    hydrateDeferredCarouselPhotos(card, photosEl);

    const allPhotos = readPhotoSources(photosEl, card);
    if (allPhotos.length <= 1) return;

    const firstSlide = container.querySelector<HTMLElement>(".swiper-slide");
    if (!firstSlide) return;

    card.dataset.carouselInit = "true";

    const { displayPhotos } = getDisplayPhotos(allPhotos, MAX_DISPLAY_PHOTOS);

    const track = document.createElement("div");
    track.className = "swiper-track";
    container.insertBefore(track, firstSlide);
    track.appendChild(firstSlide);

    const firstImg = firstSlide.querySelector<HTMLImageElement>("img");
    if (firstImg) applyPhotoSources(firstImg, displayPhotos[0]);

    for (let i = 1; i < displayPhotos.length; i++) {
        const clone = firstSlide.cloneNode(true) as HTMLElement;
        const img = clone.querySelector<HTMLImageElement>("img");
        if (img) {
            applyPhotoSources(img, displayPhotos[i]);
            img.loading = "lazy";
        }
        track.appendChild(clone);
    }

    const counterCurrentEl = card.querySelector<HTMLElement>(
        ".vehicle-card__photo-counter__current",
    );
    const overlay = card.querySelector<HTMLElement>(".vehicle-card__overlay");
    const prevBtn = card.querySelector<HTMLButtonElement>(".nav-button.prev");
    const nextBtn = card.querySelector<HTMLButtonElement>(".nav-button.next");

    const isAtCap = displayPhotos.length >= MAX_DISPLAY_PHOTOS;
    let current = 0;

    // % is relative to the track box (= one viewport width per 100%); reflows with CSS.
    const slideOffsetPercent = (idx: number): number => -idx * 100;

    const setTransform = (
        offsetPercent: number,
        dragPx: number,
        animated: boolean,
    ): void => {
        track.style.transition = animated
            ? `transform ${SLIDE_TRANSITION_MS}ms ease-out`
            : "none";
        track.style.transform =
            dragPx !== 0
                ? `translate3d(calc(${offsetPercent}% + ${dragPx}px), 0, 0)`
                : `translate3d(${offsetPercent}%, 0, 0)`;
    };

    const updateMeta = (): void => {
        if (counterCurrentEl)
            counterCurrentEl.textContent = String(current + 1);
        if (overlay) {
            overlay.classList.toggle(
                "vehicle-card__overlay--visible",
                isAtCap && current === displayPhotos.length - 1,
            );
        }
    };

    const goTo = (idx: number): void => {
        current = resolveSlideIndex(idx, displayPhotos.length);
        setTransform(slideOffsetPercent(current), 0, true);
        updateMeta();
    };

    setTransform(0, 0, false);
    updateMeta();

    prevBtn?.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        goTo(current - 1);
    });
    nextBtn?.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        goTo(current + 1);
    });

    let startX = 0;
    let startY = 0;
    let dragging = false;
    let locked = false;
    let dragDx = 0;
    let didSwipe = false;

    const onTouchStart = (e: TouchEvent): void => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        dragging = false;
        locked = false;
        dragDx = 0;
        didSwipe = false;
    };

    const onTouchMove = (e: TouchEvent): void => {
        if (e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        if (!locked) {
            if (Math.abs(dx) <= SWIPE_LOCK_PX) return;
            if (Math.abs(dx) <= Math.abs(dy)) return;
            locked = true;
            dragging = true;
        }

        if (e.cancelable) e.preventDefault();

        let effective = dx;
        const atStart = current === 0 && dx > 0;
        const atEnd = current === displayPhotos.length - 1 && dx < 0;
        if (atStart || atEnd) {
            effective = dx * RESISTANCE_RATIO;
        }
        dragDx = effective;
        setTransform(slideOffsetPercent(current), effective, false);
    };

    const onTouchEnd = (): void => {
        if (!dragging) return;
        dragging = false;
        locked = false;

        const width = touchSurface.clientWidth || 1;
        const ratio = dragDx / width;

        if (ratio <= -SWIPE_RATIO && current < displayPhotos.length - 1) {
            didSwipe = true;
            goTo(current + 1);
        } else if (ratio >= SWIPE_RATIO && current > 0) {
            didSwipe = true;
            goTo(current - 1);
        } else {
            // Snap back without changing slide.
            setTransform(slideOffsetPercent(current), 0, true);
        }

        // Suppress the synthetic click that follows a real swipe so the
        // <a class="slide-link"> wrapping each slide does not navigate.
        if (didSwipe) {
            const suppress = (ev: Event): void => {
                ev.preventDefault();
                ev.stopPropagation();
            };
            touchSurface.addEventListener("click", suppress, {
                capture: true,
                once: true,
            });
            window.setTimeout(() => {
                touchSurface.removeEventListener("click", suppress, true);
                didSwipe = false;
            }, 350);
        }
        dragDx = 0;
    };

    const onTouchCancel = (): void => {
        if (!dragging) return;
        dragging = false;
        locked = false;
        dragDx = 0;
        setTransform(slideOffsetPercent(current), 0, true);
    };

    touchSurface.addEventListener("touchstart", onTouchStart, { passive: true });
    touchSurface.addEventListener("touchmove", onTouchMove, { passive: false });
    touchSurface.addEventListener("touchend", onTouchEnd, { passive: true });
    touchSurface.addEventListener("touchcancel", onTouchCancel, {
        passive: true,
    });
}

export function setupVehicleCardCarousel(card: HTMLElement): void {
    if (card.dataset.carouselInit === "true") return;

    const photosEl = card.querySelector<HTMLElement>(".vehicle-card__photos");
    if (!photosEl) return;

    const photoCount =
        photosEl.querySelectorAll("span").length || parsePhotoKeys(card).length;
    if (photoCount <= 1) return;

    const startCarousel = (): void => {
        runVehicleCardCarouselInit(card);
    };

    if (photosEl.dataset.deferred === "true") {
        // setupAll runs on every InstantSearch render; only schedule the
        // deferred init once per card or observers/listeners pile up.
        if (card.dataset.carouselDeferredScheduled === "true") return;
        card.dataset.carouselDeferredScheduled = "true";

        const isNearViewport = (): boolean => {
            const rect = card.getBoundingClientRect();
            const margin = 200;
            return (
                rect.bottom >= -margin &&
                rect.top <= window.innerHeight + margin
            );
        };

        const scheduleDeferredCarousel = (): void => {
            if (isNearViewport()) {
                startCarousel();
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        observer.disconnect();
                        startCarousel();
                    }
                },
                { rootMargin: `${DEFERRED_CAROUSEL_ROOT_MARGIN}` },
            );
            observer.observe(card);

            const touchSurface =
                card.querySelector<HTMLElement>(".carousel-wrapper") ??
                card.querySelector<HTMLElement>(".swiper-container");
            const prevBtn = card.querySelector<HTMLButtonElement>(
                ".nav-button.prev",
            );
            const nextBtn = card.querySelector<HTMLButtonElement>(
                ".nav-button.next",
            );

            touchSurface?.addEventListener("touchstart", startCarousel, {
                once: true,
                passive: true,
            });
            prevBtn?.addEventListener("click", startCarousel, { once: true });
            nextBtn?.addEventListener("click", startCarousel, { once: true });
        };

        scheduleDeferredCarousel();
        return;
    }

    startCarousel();
}

/**
 * Initialises every `.vehicle-card` carousel currently in the DOM.
 * Idempotent — safe to call repeatedly (e.g. after InstantSearch re-renders).
 */
export function setupAllVehicleCardCarousels(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(".vehicle-card").forEach((card) => {
        setupVehicleCardCarousel(card);
        setupVehicleCardNavigation(card);
    });
}
